import { Link, useNavigate } from "react-router-dom";
import { Show, UserButton } from "@clerk/react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  GraduationCap,
  Beaker,
  ShieldCheck,
  Timer,
  LineChart,
  Sparkles,
  ArrowRight,
  CircleCheck,
} from "lucide-react";

const PLANS = [
  { id: "monthly", name: "Monthly", price: "₹499", period: "/month", badge: null, exams: "50 exams over 30 days" },
  { id: "half", name: "6 Months", price: "₹2,699", period: "/6 months", badge: "Best value", exams: "350 exams over 180 days" },
  { id: "annual", name: "Annual", price: "₹5,599", period: "/year", badge: null, exams: "700 exams over 360 days" },
];

const FEATURES = [
  { icon: ShieldCheck, title: "Real exam pattern", body: "Blueprint-driven mocks that match UGC NET and CSIR NET section rules, attempt limits and negative marking." },
  { icon: Timer, title: "Focused exam player", body: "A calm, timer-first interface with autosave and question palette — comfortable for 2–3 hour sessions." },
  { icon: LineChart, title: "Honest progress", body: "Per-section accuracy, time-per-question, unit strengths and weaknesses. No vanity metrics." },
  { icon: Sparkles, title: "AI that helps you learn", body: "AI study plans and syllabus-tagged recommendations grounded in your actual measured performance." },
];

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAFAFC] text-slate-900">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <div className="h-9 w-9 rounded-lg bg-blue-600 text-white grid place-items-center font-bold font-display">J</div>
            <span className="font-display font-bold text-lg tracking-tight">JRFHITS<span className="text-blue-600">.COM</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm text-slate-600">
            <a href="#exams" className="hover:text-slate-900">Exams</a>
            <a href="#features" className="hover:text-slate-900">Features</a>
            <a href="#pricing" className="hover:text-slate-900">Pricing</a>
          </nav>
          <div className="flex items-center gap-3">
            <Show when="signed-out">
              <Button
                variant="ghost"
                onClick={() => navigate("/sign-in")}
                data-testid="landing-sign-in-btn"
                className="text-slate-700"
              >
                Sign in
              </Button>
              <Button
                onClick={() => navigate("/sign-up")}
                data-testid="landing-get-started-btn"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Get started
              </Button>
            </Show>
            <Show when="signed-in">
              <Button
                variant="outline"
                onClick={() => navigate("/app")}
                data-testid="landing-open-dashboard-btn"
              >
                Open dashboard
              </Button>
              <UserButton />
            </Show>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-70 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700 mb-5" data-testid="landing-badge">
                UGC NET · Joint CSIR–UGC NET
              </Badge>
              <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.08]">
                Prepare smarter.
                <br />
                Practise with precision.
                <br />
                <span className="text-blue-600">Perform with confidence.</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed" data-testid="landing-hero-description">
                JRFHITS.COM gives UGC NET and CSIR NET aspirants exam-pattern-based practice, answer
                explanations, clear performance tracking and AI-personalised study suggestions — so
                every session moves you closer to your goal.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button
                  size="lg"
                  onClick={() => navigate("/sign-up")}
                  data-testid="landing-hero-cta-btn"
                  className="bg-blue-600 hover:bg-blue-700 text-white h-12 px-6"
                >
                  Get started free <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate("/sign-in")}
                  data-testid="landing-hero-signin-btn"
                  className="h-12 px-6"
                >
                  I already have an account
                </Button>
              </div>
              <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
                <li className="flex items-center gap-2"><CircleCheck className="h-4 w-4 text-emerald-600" /> 3 free attempts, no card needed</li>
                <li className="flex items-center gap-2"><CircleCheck className="h-4 w-4 text-emerald-600" /> Real section rules & marking</li>
                <li className="flex items-center gap-2"><CircleCheck className="h-4 w-4 text-emerald-600" /> Secure Google & email sign-in</li>
              </ul>
            </div>

            {/* Exam family cards */}
            <div className="lg:col-span-5" id="exams">
              <div className="grid sm:grid-cols-2 gap-4">
                <Card className="p-6 border-slate-200 hover:border-blue-300 transition-colors" data-testid="exam-family-ugc-net-card">
                  <div className="h-10 w-10 rounded-lg bg-blue-50 text-blue-600 grid place-items-center">
                    <GraduationCap className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-display font-semibold text-lg">UGC NET</h3>
                  <p className="text-sm text-slate-600 mt-1">87 subjects · Paper I shared · Paper II subject-specific</p>
                  <p className="text-xs text-slate-500 mt-3 font-mono">150 Q · 300 marks · 180 min</p>
                </Card>
                <Card className="p-6 border-slate-200 hover:border-blue-300 transition-colors" data-testid="exam-family-csir-net-card">
                  <div className="h-10 w-10 rounded-lg bg-teal-50 text-teal-600 grid place-items-center">
                    <Beaker className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-display font-semibold text-lg">CSIR NET</h3>
                  <p className="text-sm text-slate-600 mt-1">5 subjects · Part A shared · Parts B & C subject-specific</p>
                  <p className="text-xs text-slate-500 mt-3 font-mono">200 marks · 180 min</p>
                </Card>
              </div>
              <p className="mt-4 text-xs text-slate-500 leading-relaxed">
                Subject-wise content appears after you pick your exam family and subjects. Blueprints
                and syllabi are versioned and reconciled against the applicable bulletin.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="font-medium text-xs uppercase tracking-wider text-slate-500">Built for serious preparation</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl mt-3 tracking-tight text-slate-900">
              Everything a candidate actually needs. Nothing they don't.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((f) => (
              <Card key={f.title} className="p-6 border-slate-200" data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}-card`}>
                <div className="h-10 w-10 rounded-lg bg-slate-100 text-slate-700 grid place-items-center">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display font-semibold text-lg">{f.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.body}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="font-medium text-xs uppercase tracking-wider text-slate-500">Honest pricing</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl mt-3 tracking-tight text-slate-900">Start free. Upgrade when you're ready.</h2>
            <p className="mt-4 text-slate-600">Three free qualifying attempts for every new student. After that, pick the plan that fits your timeline.</p>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {PLANS.map((p) => (
              <Card
                key={p.id}
                className={`p-7 border-slate-200 relative ${p.badge ? "ring-2 ring-blue-500" : ""}`}
                data-testid={`pricing-plan-${p.id}-card`}
              >
                {p.badge && (
                  <Badge className="absolute -top-3 left-6 bg-blue-600 hover:bg-blue-600 text-white">{p.badge}</Badge>
                )}
                <h3 className="font-display font-semibold text-lg">{p.name}</h3>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-display font-extrabold text-4xl tracking-tight">{p.price}</span>
                  <span className="text-sm text-slate-500">{p.period}</span>
                </div>
                <ul className="mt-6 space-y-2.5 text-sm text-slate-600">
                  <li className="flex items-start gap-2"><CircleCheck className="h-4 w-4 text-emerald-600 mt-0.5" /> {p.exams}</li>
                  <li className="flex items-start gap-2"><CircleCheck className="h-4 w-4 text-emerald-600 mt-0.5" /> All subjects & full mocks</li>
                  <li className="flex items-start gap-2"><CircleCheck className="h-4 w-4 text-emerald-600 mt-0.5" /> AI study plans from your results</li>
                  <li className="flex items-start gap-2"><CircleCheck className="h-4 w-4 text-emerald-600 mt-0.5" /> No-repeat question history</li>
                  <li className="flex items-start gap-2"><CircleCheck className="h-4 w-4 text-emerald-600 mt-0.5" /> Progress analytics</li>
                </ul>
                <Button
                  className="mt-7 w-full bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => navigate("/sign-up")}
                  data-testid={`pricing-plan-${p.id}-btn`}
                >
                  Get started
                </Button>
              </Card>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-500" data-testid="landing-payment-note">Payment via secured PayU.</p>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-blue-600 text-white grid place-items-center text-xs font-bold font-display">J</div>
            <span className="font-display font-semibold text-sm">JRFHITS.COM</span>
          </div>
          <p className="text-xs text-slate-500">© {new Date().getFullYear()} JRFHITS.COM · Reconciled against applicable UGC/CSIR bulletins.</p>
        </div>
      </footer>
    </div>
  );
}
